import { formatFirmwareVersionLabel } from '../pages/firmwareQuery';

const GROUP_META = {
  wifi: {
    label: 'Wi-Fi',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200',
  },
  nbiot: {
    label: 'NB-IoT',
    badge: 'bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200',
  },
};

const DEFAULT_RISK_WORDS = ['Связь', 'Показания', 'Сигнал', 'Батарея'];

function formatCount(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return '—';
  }
  return new Intl.NumberFormat('ru-RU').format(Number(value));
}

function riskPercentStyle(percent) {
  const value = Math.min(100, Math.max(0, Number(percent) || 0));
  if (value <= 0) {
    return { color: 'hsl(142 55% 36%)' };
  }
  const t = value / 100;
  const hue = Math.round(142 * (1 - t));
  const saturation = Math.round(62 + 28 * t);
  const lightness = Math.round(38 - 6 * t);
  return { color: `hsl(${hue} ${saturation}% ${lightness}%)` };
}

function sparkTone(values) {
  const points = (values || []).filter((value) => value != null && Number.isFinite(Number(value)));
  if (points.length < 2) {
    return '#64748b';
  }
  const delta = Number(points[points.length - 1]) - Number(points[0]);
  if (delta > 0.2) {
    return '#dc2626';
  }
  if (delta < -0.2) {
    return '#16a34a';
  }
  return '#475569';
}

/** Compact sparkline matching the Telegram versions photo. */
function RiskSpark({ values, width = 44, height = 16 }) {
  const points = [];
  for (let index = 0; index < (values || []).length; index += 1) {
    const value = values[index];
    if (value == null || !Number.isFinite(Number(value))) {
      continue;
    }
    points.push([index, Number(value)]);
  }
  if (points.length < 2) {
    return <span className="inline-block h-4 w-11" aria-hidden="true" />;
  }

  let lo = Math.min(...points.map((item) => item[1]));
  let hi = Math.max(...points.map((item) => item[1]));
  if (Math.abs(hi - lo) < 0.5) {
    lo -= 2;
    hi += 2;
  }
  const span = hi - lo || 1;
  const stepCount = Math.max(1, values.length - 1);
  const coords = points.map(([index, value]) => {
    const x = (index * width) / stepCount;
    const y = height - ((value - lo) / span) * (height - 2) - 1;
    return [x, y];
  });
  const color = sparkTone(values);
  const line = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `0,${height} ${line} ${coords[coords.length - 1][0].toFixed(1)},${height}`;
  const [lx, ly] = coords[coords.length - 1];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="shrink-0" aria-hidden="true">
      <polygon points={area} fill={color} fillOpacity="0.16" />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth="1.3"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lx} cy={ly} r="1.3" fill={color} />
    </svg>
  );
}

function AllRisksGroupCard({ group, riskWords, organizationId, VersionCell }) {
  const meta = GROUP_META[group?.key] || GROUP_META.wifi;
  const rows = group?.rows || [];
  const words = riskWords?.length ? riskWords : DEFAULT_RISK_WORDS;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/70">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-200 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-900/50">
        <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${meta.badge}`}>{meta.label}</span>
        <span className="text-xs text-slate-600 dark:text-slate-300">
          <span className="font-semibold tabular-nums text-slate-900 dark:text-white">
            {formatCount(group?.total)}
          </span>{' '}
          · {formatCount(rows.length)} вер.
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem] text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-400 dark:border-slate-800">
              <th className="px-3 py-1.5 font-medium">Версия</th>
              <th className="w-16 px-3 py-1.5 text-right font-medium">Всего</th>
              {words.map((word) => (
                <th key={word} className="px-2 py-1.5 font-medium">
                  {word}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2 + words.length} className="px-3 py-6 text-center text-sm text-slate-400">
                  Нет устройств этого типа связи
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={`${group.key}-${row.version}`}
                  className="border-b border-slate-50 odd:bg-slate-50/40 last:border-0 dark:border-slate-800/60 dark:odd:bg-slate-900/30"
                >
                  <td className="px-3 py-1.5">
                    <VersionCell
                      organizationId={organizationId}
                      firmwareVersion={row.version}
                      connectionType={group.key}
                      riskNumber="1"
                      label={formatFirmwareVersionLabel(row.version)}
                    />
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600 dark:text-slate-300">
                    {formatCount(row.total)}
                  </td>
                  {(row.percents || []).map((percent, riskIndex) => {
                    const riskNumber = String(riskIndex + 1);
                    const series = row.series?.[riskIndex] || [];
                    return (
                      <td key={`${row.version}-${riskNumber}`} className="px-2 py-1">
                        <VersionCell
                          organizationId={organizationId}
                          firmwareVersion={row.version}
                          connectionType={group.key}
                          riskNumber={riskNumber}
                          className="inline-flex items-center gap-1.5 rounded-md border border-slate-100 bg-white px-1.5 py-0.5 dark:border-slate-800 dark:bg-slate-950"
                          label={
                            <>
                              <span
                                className="min-w-[2rem] text-[12px] font-semibold tabular-nums"
                                style={riskPercentStyle(percent)}
                              >
                                {percent}%
                              </span>
                              <RiskSpark values={series} />
                            </>
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Telegram-style versions matrix: Wi-Fi / NB-IoT cards with all four risks as columns.
 * VersionCell: button/link renderer (LK passes onOpenVersion wrapper).
 */
export default function VersionsAllRisksTable({
  report,
  organizationId,
  VersionCell,
}) {
  if (typeof VersionCell !== 'function') {
    throw new Error('VersionsAllRisksTable requires VersionCell');
  }
  if (!report) {
    return null;
  }

  const riskWords = report.risk_words || DEFAULT_RISK_WORDS;

  return (
    <div className="space-y-4">
      {report.wifi ? (
        <AllRisksGroupCard
          group={report.wifi}
          riskWords={riskWords}
          organizationId={organizationId}
          VersionCell={VersionCell}
        />
      ) : null}
      {report.nbiot ? (
        <AllRisksGroupCard
          group={report.nbiot}
          riskWords={riskWords}
          organizationId={organizationId}
          VersionCell={VersionCell}
        />
      ) : null}
    </div>
  );
}

export { RiskSpark, riskPercentStyle, formatCount };
